import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLink, UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {useCountries, useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {getAttendeeAssignProgress, getAttendeeAssignResult} from 'src/openapi/admin/apis/BulkAttendeeAssignApi';
import {useAppDispatch} from 'src/store/hooks';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {FILE_HEADERS, FILE_NAME, TOOL_NAME} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ProcessedAttendeeItem} from 'src/openapi/admin/models/ProcessedAttendeeItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const AddAttendeesResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const {getUserStatusByCode} = useUserStatuses();
    const {getUserTypeByCode} = useUserTypes();
    const {getCountryByCode} = useCountries();

    const getPaginatedResults = useCallback(
        (bulkResult: ProcessedAttendeeItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: ProcessedAttendeeItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        {res.userId ? (
                            <UserLink
                                userId={res.userId}
                                caption={res.email}
                                blank={true}
                                place={`${TOOL_NAME} Result`}
                            />
                        ) : (
                            <EllipsisTextHint title={res.email}>
                                {res.email}
                            </EllipsisTextHint>
                        )}
                    </Cell>
                    <Cell>{!!res.primaryAccountId && !!res.primaryAccountName && (
                        <AccountLink
                            accountId={res.primaryAccountId}
                            accountName={res.primaryAccountName}
                            blank={true}
                            place={`${TOOL_NAME} Result`}
                        />
                    )}
                    </Cell>
                    <Cell>
                        {getUserTypeByCode(res.typeCode)?.description} ({
                            getUserStatusByCode(res.statusCode)?.description
                        })
                    </Cell>
                    <Cell>
                        <CompleteMark errors={res.errors} tooltip={res.jobInfo}/>
                    </Cell>
                </Row>
            )),
        [getUserStatusByCode, getUserTypeByCode],
    );

    return (
        <BulkActionResultModal<ProcessedAttendeeItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_NAME}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Result of the linkage of users to {bulkResult.itemList && bulkResult.itemList[0] ? (
                        <AccountLink
                            accountId={bulkResult.itemList[0].conferenceAccountId}
                            accountName={bulkResult.itemList[0].conferenceAccountName}
                            blank={true}
                            place={`${TOOL_NAME} Result`}
                        />
                    ) : null} account
                    </b>
                    <BulkActionDownload
                        toolName={TOOL_NAME}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            userId: item.userId ? item.userId.toString() : '',
                            email: item.email,
                            firstName: item.firstName,
                            lastName: item.lastName,
                            userType: getUserTypeByCode(item.typeCode)?.description,
                            userStatus: getUserStatusByCode(item.statusCode)?.description,
                            primaryAccountId: item.primaryAccountId?.toString(),
                            primaryAccountName: item.primaryAccountName,
                            conferenceAccountId: item.conferenceAccountId?.toString(),
                            conferenceAccountName: item.conferenceAccountName,
                            country: item.countryCode
                                ? getCountryByCode(item.countryCode)?.description
                                : '--',
                            jobTitle: item.jobTitle,
                            city: item.city,
                            notes: item.notes,
                            actionStatus: item.actionStatus,
                            jobInfo: item.jobInfo?.join('. '),
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={200}>Email</Cell>
                        <Cell width={200}>Primary Account</Cell>
                        <Cell width={200}>User State</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getAttendeeAssignProgress({id}))}
            getResult={() => apiDispatch(getAttendeeAssignResult({id}))}

            sortComparator={(a, b) => a.email.localeCompare(b.email)}
        />
    );
};
